import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Car,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  FileText,
  Calculator,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  const num = parseFloat(amount);
  if (isNaN(num)) return 'N/A';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export default function VehicleReassessmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  
  const policyNumber = searchParams.get('policy');
  
  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [claimHistory, setClaimHistory] = useState(null);
  
  // Form data
  const [newValue, setNewValue] = useState('');
  const [assessmentNotes, setAssessmentNotes] = useState('');

  useEffect(() => {
    if (policyNumber) {
      fetchPolicyDetails();
    } else {
      setError('No policy specified');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyNumber]);

  const fetchPolicyDetails = async () => {
    const token = getToken();
    if (!token) {
      navigate('/portal/login');
      return;
    }

    try {
      setLoading(true);
      
      // Get policy details
      const policyResponse = await fetch(`${API_URL}/api/claims/policy/${policyNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!policyResponse.ok) {
        throw new Error('Failed to load policy details');
      }
      
      const policyData = await policyResponse.json();
      setPolicy(policyData.policy);
      
      // Set initial value suggestion (current value minus 10-20%)
      const currentValue = policyData.policy?.vehicle?.estimated_value || 0;
      const suggestedValue = Math.round(currentValue * 0.85);
      setNewValue(suggestedValue.toString());
      
      // Get claim history
      const vin = policyData.policy?.vehicle?.vin;
      if (vin) {
        const historyResponse = await fetch(`${API_URL}/api/claims/vehicle-history/${vin}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setClaimHistory(historyData.history);
        }
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = getToken();
    if (!token) {
      navigate('/portal/login');
      return;
    }

    const valueNum = parseFloat(newValue);
    if (isNaN(valueNum) || valueNum <= 0) {
      setError('Please enter a valid vehicle value');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/claims/reassessment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          policy_number: policyNumber,
          new_value: valueNum,
          assessment_notes: assessmentNotes
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to complete reassessment');
      }
      
      setSuccess(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-[#3B5BDB] animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  if (success) {
    return (
      <PortalLayout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="shadow-xl border-0 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3">Reassessment Complete</h1>
              <p className="text-slate-600 mb-6">
                Your vehicle value has been updated. You can now file a new claim for this vehicle.
              </p>
              
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-500 mb-1">New Vehicle Value</p>
                <p className="text-2xl font-bold text-[#3B5BDB]">{formatCurrency(newValue)}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate(`/claims/file?policy=${policyNumber}`)}
                  className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
                  data-testid="file-claim-btn"
                >
                  File a Claim
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/portal')}
                  className="rounded-xl"
                >
                  Return to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ChevronLeft className="h-5 w-5" />
          Back
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Calculator className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Vehicle Reassessment</h1>
              <p className="text-slate-600">Update your vehicle's value before filing a new claim</p>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="p-6">
                {/* Vehicle Info */}
                {policy?.vehicle && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Car className="h-5 w-5 text-slate-500" />
                      <h3 className="font-semibold text-slate-900">Vehicle Information</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Vehicle</p>
                        <p className="font-medium text-slate-900">
                          {policy.vehicle.year} {policy.vehicle.make} {policy.vehicle.model}{policy.vehicle.trim ? ` ${policy.vehicle.trim}` : ''}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">VIN</p>
                        <p className="font-mono text-sm text-slate-700">{policy.vehicle.vin}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Current Value</p>
                        <p className="font-semibold text-slate-900">
                          {formatCurrency(policy.vehicle.estimated_value)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Policy</p>
                        <p className="font-mono text-sm text-slate-700">{policyNumber}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Why Reassessment?</p>
                      <p>
                        After a claim has been settled, vehicle values may change due to market conditions, 
                        prior damage history, or depreciation. A reassessment ensures accurate coverage 
                        limits for any future claims.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="new_value" className="text-slate-700 font-medium">
                      New Vehicle Value <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="new_value"
                        type="number"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="Enter new vehicle value"
                        className="pl-10 h-12 rounded-xl"
                        min="0"
                        step="100"
                        required
                        data-testid="new-value-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Based on current market conditions and claim history
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="notes" className="text-slate-700 font-medium">
                      Assessment Notes (Optional)
                    </Label>
                    <Textarea
                      id="notes"
                      value={assessmentNotes}
                      onChange={(e) => setAssessmentNotes(e.target.value)}
                      placeholder="Any relevant notes about the assessment..."
                      className="mt-1 rounded-xl min-h-[100px]"
                      data-testid="assessment-notes"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting || !newValue}
                    className="w-full h-12 bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl font-semibold"
                    data-testid="submit-reassessment-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Complete Reassessment
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Claim History */}
          <div>
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-5 w-5 text-slate-500" />
                  <h3 className="font-semibold text-slate-900">Claim History</h3>
                </div>

                {claimHistory?.claims?.length > 0 ? (
                  <div className="space-y-3">
                    {claimHistory.claims.slice(0, 5).map((claim) => (
                      <div 
                        key={claim.claim_number}
                        className="p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-sm text-slate-700">
                            {claim.claim_number}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            claim.status === 'paid' || claim.status === 'settled' 
                              ? 'bg-green-100 text-green-700'
                              : claim.status === 'closed'
                                ? 'bg-slate-100 text-slate-700'
                                : 'bg-blue-100 text-blue-700'
                          }`}>
                            {claim.status_label}
                          </span>
                        </div>
                        {claim.paid_amount > 0 && (
                          <p className="text-xs text-slate-500">
                            Paid: {formatCurrency(claim.paid_amount)}
                          </p>
                        )}
                      </div>
                    ))}
                    
                    {claimHistory.total_paid_amount > 0 && (
                      <div className="pt-3 border-t border-slate-200">
                        <p className="text-sm text-slate-600">
                          Total Paid: <span className="font-semibold">{formatCurrency(claimHistory.total_paid_amount)}</span>
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No previous claims found</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
